import { useState } from 'react';
import useFindOrdersQuery from '@/api/queries/useFindOrdersQuery';
import useOrderMetricsQuery from '@/api/queries/useOrderMetricsQuery';
import Button from '@/components/Button';
import DailyOrderMetricsStats from '@/components/dashboard/DailyOrderMetricsStats';
import LatestOrdersStats from '@/components/dashboard/LatestOrdersStats';
import StatCard from '@/components/dashboard/StatCard';
import MultiDateRangePicker from '@/components/datepicker/MultiDateRangePicker';
import PageHeader from '@/components/PageHeader';
import { useSettings } from '@/contexts/SettingsContext';
import { RetailerSettingsPath } from '@/modules/retailer/paths';
import { formatPrice } from '@/utils/currency';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import {
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/solid';

const now = new Date();
const MIN_DATE = new Date(2020, 0, 1);
const MAX_METRICS_RANGE_IN_MONTHS = 36;
const DEFAULT_START = new Date(now.getFullYear(), 0, 1);
const DEFAULT_END = new Date(now.getFullYear(), now.getMonth() + 1, 0);
const DATE_LOCALE = 'en-GB';
const LAST_ORDERS_LIMIT = 5;

interface Period {
  startDate: Date | null;
  endDate: Date | null;
}

interface TimeWindow {
  start: Date;
  end: Date;
}

function toTimeWindow(dateRange: Period): TimeWindow | undefined {
  return dateRange.startDate && dateRange.endDate
    ? { start: dateRange.startDate, end: dateRange.endDate }
    : undefined;
}

const Orders = () => {
  const [dateRange, setDateRange] = useState<Period>({
    startDate: DEFAULT_START,
    endDate: DEFAULT_END,
  });
  const { currencyCode } = useSettings();

  const {
    daily,
    isLoading: isLoadingMetrics,
    totalOrderCount,
    totalOrderAmount,
    storeCount,
  } = useOrderMetricsQuery(dateRange.startDate, dateRange.endDate, currencyCode);
  const { orders, isLoading: isLoadingOrders } = useFindOrdersQuery(1, undefined, {
    period: toTimeWindow(dateRange),
  });

  const formatPeriod = (period: Period) =>
    period.startDate &&
    period.endDate &&
    `${period.startDate.toLocaleDateString(DATE_LOCALE)} -> ${period.endDate.toLocaleDateString(
      DATE_LOCALE,
    )}`;

  return (
    <div className="space-y-6">
      <PageHeader title="Orders" />

      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <MultiDateRangePicker
          value={dateRange}
          onChange={setDateRange}
          minDate={MIN_DATE}
          maxRangeInMonths={MAX_METRICS_RANGE_IN_MONTHS}
        />

        <div className="text-sm text-gray-700 flex flex-col space-y-2">
          <div className="flex items-center space-x-1">
            <CalendarDaysIcon className="h-5 w-5 text-gray-500" />
            <span>{formatPeriod(dateRange)}</span>
          </div>

          {currencyCode && (
            <div>
              <Button
                size="sm"
                variant="secondary"
                to={RetailerSettingsPath.currency}
                icon={<CurrencyDollarIcon className="h-4 w-4 text-gray-500" />}
                className="shadow-sm font-normal"
              >
                Currency: {currencyCode}
              </Button>
            </div>
          )}
        </div>
      </div>

      <section className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Summary</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="Orders in Period"
              value={isLoadingMetrics ? '—' : totalOrderCount.toLocaleString()}
              icon={<ShoppingBagIcon className="h-8 w-8 text-indigo-500" />}
              loading={isLoadingMetrics}
            />

            <StatCard
              title="Revenue in Period"
              value={isLoadingMetrics ? '—' : formatPrice(totalOrderAmount, currencyCode)}
              icon={<ChartBarIcon className="h-8 w-8 text-blue-500" />}
              loading={isLoadingMetrics}
            />

            <StatCard
              title="Active Stores"
              value={isLoadingMetrics ? '—' : storeCount.toString()}
              icon={<BuildingStorefrontIcon className="h-8 w-8 text-green-500" />}
              loading={isLoadingMetrics}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">
              Last {LAST_ORDERS_LIMIT} days with orders
            </h3>

            <DailyOrderMetricsStats
              daily={daily || undefined}
              isLoading={isLoadingMetrics}
              currencyCode={currencyCode}
              limit={LAST_ORDERS_LIMIT}
            />
          </div>

          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">
              Latest {LAST_ORDERS_LIMIT} orders
            </h3>

            <LatestOrdersStats
              orders={orders.items}
              isLoading={isLoadingOrders}
              limit={LAST_ORDERS_LIMIT}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Orders;
